import { format } from 'date-fns'
import { parseDate } from '@/shared/datetime'
import { indexDay } from '@/shared/templates/memos/lib/indexDay'

/**
 * The day the memo itself belongs to, which is where its planning is created
 * and what its assignment is dated - not the days it covers. A weekend PM is
 * put together on the Thursday for the Friday through Sunday it lists, and
 * belongs on that Thursday; moving the range in the dialog says nothing about
 * it.
 *
 * A planning the memo is filed into says the day outright, which is why it wins
 * over the day the editor had open when the dialog was opened. Its `start_date`
 * is read the way every other index date is - see `indexDay`, and `toMemoItems`
 * for the same rule on the candidates' own days.
 */
export function resolveMemoDay({ planningStartDate, queryFrom, today, timeZone }: {
  /** `document.meta.core_planning_item.data.start_date` of the filed-into planning. */
  planningStartDate?: string
  /** The day the editor had open, as the `from` query parameter carries it. */
  queryFrom?: string | string[]
  today: string
  timeZone: string
}): string {
  const filedUnder = planningStartDate
    ? indexDay(planningStartDate, timeZone)
    : undefined

  if (filedUnder) {
    return filedUnder
  }

  // The day the editor had open is whatever the URL says, so it is parsed
  // before it is believed - the same guard the header's date changer puts on
  // `from`. Read as well as taken: `parseDate` answers a string that is no date
  // at all with an Invalid Date rather than with nothing, so the parse alone
  // would let one through.
  //
  // And formatted back rather than handed on as written, since what parses is
  // not only what is written `YYYY-MM-DD` - "2026-9-3" parses, and carried on
  // as it came it is a day the rest of the dialog cannot parse at all.
  //
  // A `from` naming no day falls back to today, which is the registry's own -
  // `new Date()` would be the browser's, a day out either side of midnight for
  // an editor sitting on another timezone.
  if (typeof queryFrom === 'string') {
    const opened = parseDate(queryFrom)

    if (opened && !isNaN(opened.getTime())) {
      return format(opened, 'yyyy-MM-dd')
    }
  }

  return today
}
